import { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import type { Lane, Card } from "../lib/types";
import { LaneColumn } from "./Lane";

// Layout constants. These match the visual rhythm of the rest of the
// app (12px gaps, rounded-lane radius) and set the "readable floor"
// for a lane before we spill into another masonry column.
const MIN_LANE_WIDTH = 288; // px — one lane's minimum comfortable width
const GAP = 12; // px — column and row gap between lanes (Tailwind gap-3)

type Props = {
  lanes: Lane[];
  cardsByLane: Map<string | null, Card[]>;
  onAddCard: (laneId: string, title: string) => void;
  onCardClick: (card: Card) => void;
  onAddLane: () => void;
  onRenameLane: (laneId: string, name: string) => void;
  onChangeAccent: (laneId: string, accent: Lane["accent"]) => void;
  onDeleteLane: (laneId: string) => void;
};

/**
 * Column-major responsive masonry for lanes.
 *
 * The user wants three behaviours together:
 *   1. Lanes fill available width (no horizontal scroll).
 *   2. Each lane has a minimum comfortable width; when they'd shrink
 *      below that, one wraps to a new row.
 *   3. Wrap order is *column-major by index*: lane 4 sits directly
 *      under lane 1, lane 5 under lane 2, etc. — never jumbled.
 *
 * CSS Grid `auto-fit / minmax` handles (1) + (2) beautifully but
 * fills row-major (lane 4 goes next to lane 1, not below it), and
 * CSS multi-column fills column 1 completely before starting column
 * 2. Neither is what we want, so we compute the column count with a
 * ResizeObserver and distribute lanes via `i % N` into N side-by-side
 * flex columns. This is deterministic and dnd-kit friendly.
 *
 * Height auto-adjusts per column: expanding a card in lane 1 grows
 * only column 1's height, columns 2/3 stay short — exactly the
 * "auto-lower" behaviour requested.
 */
export function LaneGrid({
  lanes,
  cardsByLane,
  onAddCard,
  onCardClick,
  onAddLane,
  onRenameLane,
  onChangeAccent,
  onDeleteLane,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [columnCount, setColumnCount] = useState(3);

  // Observe the grid's own width and recompute how many min-width
  // lanes fit. We include the trailing gap of the last column in the
  // budget: N columns need (N * MIN + (N-1) * GAP) px, so the number
  // that fits into width w is floor((w + GAP) / (MIN + GAP)).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = (w: number) => {
      const cols = Math.max(1, Math.floor((w + GAP) / (MIN_LANE_WIDTH + GAP)));
      setColumnCount((prev) => (prev === cols ? prev : cols));
    };
    compute(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) compute(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Distribute lanes into N columns column-major by original index.
  // The "add lane" tile is a virtual (n+1)th item that flows into the
  // next slot naturally — so 5 lanes in 3 cols get:
  //   col0: [1, 4]      col1: [2, 5]      col2: [3, +add]
  const columns = useMemo(() => {
    const cols: Array<Array<{ kind: "lane"; lane: Lane } | { kind: "add" }>> =
      Array.from({ length: columnCount }, () => []);
    lanes.forEach((lane, i) => {
      cols[i % columnCount].push({ kind: "lane", lane });
    });
    // Place the add-lane button at position lanes.length in the same
    // column-major flow. If there are 0 lanes it goes at col 0.
    cols[lanes.length % columnCount].push({ kind: "add" });
    return cols;
  }, [lanes, columnCount]);

  return (
    <div
      ref={containerRef}
      // min-w-0 lets the flex-1 parent shrink us below its content's
      // natural width — required for the ResizeObserver to reflect the
      // real available space, not the widest column's demand.
      className="flex-1 min-w-0"
    >
      <SortableContext
        items={lanes.map((l) => l.id)}
        strategy={rectSortingStrategy}
      >
        <div
          className="grid items-start gap-3"
          style={{
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          }}
        >
          {columns.map((col, ci) => (
            <div key={ci} className="flex min-w-0 flex-col gap-3">
              {col.map((item) =>
                item.kind === "lane" ? (
                  <LaneColumn
                    key={item.lane.id}
                    lane={item.lane}
                    cards={cardsByLane.get(item.lane.id) ?? []}
                    onAddCard={(title) => onAddCard(item.lane.id, title)}
                    onCardClick={onCardClick}
                    onRenameLane={(name) => onRenameLane(item.lane.id, name)}
                    onChangeAccent={(a) => onChangeAccent(item.lane.id, a)}
                    onDeleteLane={() => onDeleteLane(item.lane.id)}
                  />
                ) : (
                  <button
                    key="__add_lane"
                    onClick={onAddLane}
                    className="flex h-fit items-center justify-center gap-2 rounded-lane border border-dashed border-border-strong/60 px-4 py-3 text-sm text-ink-mute transition-colors hover:border-lavender-200 hover:bg-lavender-50/30 hover:text-ink-soft"
                  >
                    <Plus size={14} /> add lane
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </SortableContext>
    </div>
  );
}
